// Utilizes intl-tel-input plugin https://intl-tel-input.com/
import React, { useEffect, useRef, useState, useMemo } from 'react';
// eslint-disable-next-line no-duplicate-imports
import type { HTMLAttributes } from 'react';
import type {
  SomeOptions as TelInputOptions,
  Iti as TelInputPlugin,
} from 'v24-intl-tel-input';
/* eslint-disable import/extensions */
import IntlTelInput, {
  intlTelInput as pluginInstance,
} from 'v24-intl-tel-input/react/build/IntlTelInputWithUtils.js';
/* eslint-enable import/extensions */
import type { Country } from 'v24-intl-tel-input/data';
import cx from 'classnames';
import { ChevronDown, Search } from '@design-systems/icons';
import 'v24-intl-tel-input/build/css/intlTelInput.css';
import { default as ReactDOM, createPortal } from 'react-dom';
import useJoinedRef from '@mc/hooks/useJoinedRef';
import useId from '@mc/hooks/useId';
import IconBadge from '../Badge/IconBadge';
import stylesheet from './InputPhone.css';

export const validationError = {
  IS_POSSIBLE: 0,
  INVALID_COUNTRY_CODE: 1,
  TOO_SHORT: 2,
  TOO_LONG: 3,
  IS_POSSIBLE_LOCAL_ONLY: 4,
  INVALID_LENGTH: 5,
};

const noop = () => {};

/**
 * More details on pre-built react component here:
 * https://github.com/jackocnr/intl-tel-input/tree/master/react
 * And additional API documentation (eg. initialization, supported instance/static methods) here:
 * https://github.com/jackocnr/intl-tel-input/blob/master/README.md
 */
export type InputPhoneProps = {
  className?: string;
  id?: string;
  /** The label of the input */
  label?: React.ReactNode;
  /** Visually hides the label provided by the `label` prop */
  hideLabel?: boolean;
  /** Will show in place of help text if defined also applies invalid style treatment */
  error?: string | boolean | React.ReactNode;
  /** Will show in place of help text if defined also applies warning style treatment */
  warning?: string | boolean;
  /** Text that appears below the input */
  helpText?: React.ReactNode;
  /** Text that appears above the input and right of the label. Usually shows Required state of the input. */
  miscText?: React.ReactNode;
  /** The required marker of the input */
  required?: boolean;
  /** The initial value of the input */
  initialValue?: string;
  /** Callback with added details sent when the phone input is initialized */
  onInitInputPhone?: ({
    supportedCountryCodes,
    listedCountries,
  }: {
    /** All available countries (total: 244) */
    supportedCountryCodes: Country[];
    /** Countries that will actually appear in the dropdown after include/exclude filtering */
    listedCountries: Country[];
  }) => void;
  /** Callback when input value is updated */
  onChangeNumber?: (number: string) => void;
  /** Triggers when country selection changes */
  onChangeCountry?: (countryData: Country) => void;
  /** Returns the validity of the input */
  onChangeValidity?: (valid: boolean) => void;
  /** Returns the error code of the input
   * @see https://github.com/jackocnr/intl-tel-input/blob/master/src/js/utils.js#L223
   */
  onChangeErrorCode?: (errorCode: string | null) => void;
  /** Automatically set the initial country of the input */
  initialCountry?: string;
  /**
   * List of country iso2 codes to include in the dropdown
   * If array is given, excludedCountriesIso will be ignored
   */
  includedCountriesIso?: string[];
  /** List of country iso2 codes to exclude from the dropdown */
  excludedCountriesIso?: string[];
  /** List of country iso2 codes to appear higher in the dropdown */
  prioritizedCountriesIso?: string[];
  /** Display the selected country's dial code next to look like part of the typed number */
  separateDialCode?: boolean;
  /** Disables the country selection dropdown */
  dropdownDisabled?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean | undefined;
  /** Readonly allows input to be selected but not modified */
  readOnly?: boolean;
  /** The placeholder text for the input */
  placeholder?: string;
  /** Size of the input */
  inputSize?: 'large' | 'medium';
} & HTMLAttributes<HTMLInputElement>;

const InputPhone = React.forwardRef<HTMLInputElement, InputPhoneProps>(
  function InputPhone(
    {
      className,
      id: idFromProps,
      label,
      hideLabel = false,
      error,
      warning = false,
      helpText,
      miscText,
      required = false,
      initialValue,
      initialCountry = 'us',
      excludedCountriesIso = [],
      includedCountriesIso = [],
      prioritizedCountriesIso = [],
      separateDialCode = false,
      dropdownDisabled = false,
      onInitInputPhone = noop,
      onChangeNumber = noop,
      onChangeCountry = noop,
      onChangeValidity = noop,
      onChangeErrorCode = noop,
      disabled,
      readOnly,
      placeholder,
      inputSize = 'large',
      // Also supports other semantic Input props
      ...props
    },
    forwardedRef,
  ) {
    const _id = useId();
    const id = idFromProps || _id;
    const labelId = useId();
    const helpTextId = useId();

    // Required to access intl-tel-input instance methods
    const ref = useRef<{
      getInstance: () => TelInputPlugin;
      getInput: () => HTMLInputElement;
    }>(null);
    const inputPhoneRef = useJoinedRef(forwardedRef, ref);

    const getItiElement = () => {
      const inputElement = ref.current?.getInput();
      if (inputElement) {
        const itiContainer = inputElement.closest('.iti');
        return itiContainer;
      }
      return null;
    };

    // The plugin does not provide a list of countries that will actually appear in the dropdown
    // after include/exclude filtering, so we need to do it ourselves
    // TODO: Is this necessary?
    const { allCountries, listedCountries } = useMemo(() => {
      // eslint-disable-next-line no-shadow
      const allCountries = pluginInstance.getCountryData();
      // eslint-disable-next-line no-shadow
      let listedCountries = allCountries;

      if (includedCountriesIso && includedCountriesIso.length > 0) {
        const includedCountriesLower = new Set(
          includedCountriesIso.map((iso) => iso.toLowerCase()),
        );
        listedCountries = allCountries.filter((country: $TSFixMe) =>
          includedCountriesLower.has(country.iso2.toLowerCase()),
        );
      } else if (excludedCountriesIso && excludedCountriesIso.length > 0) {
        const excludedCountriesLower = new Set(
          excludedCountriesIso.map((iso) => iso.toLowerCase()),
        );
        listedCountries = allCountries.filter(
          (country: $TSFixMe) =>
            !excludedCountriesLower.has(country.iso2.toLowerCase()),
        );
      }

      return { allCountries, listedCountries };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Send initial callback with all countries and listed countries
    useEffect(() => {
      onInitInputPhone({
        supportedCountryCodes: allCountries,
        listedCountries: listedCountries,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // State for portal target element
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

    // The plugin doesn't support customized components
    // Sets up portal target to insert custom arrow
    useEffect(() => {
      const itiContainer = getItiElement();
      if (itiContainer) {
        const countryButton = itiContainer?.querySelector(
          'button.iti__selected-country',
        ) as HTMLElement;

        if (countryButton) {
          setPortalTarget(countryButton);
        }
      }
    }, []);

    // Likewise, the stock search can't easily be customized
    // Manipulate the search input DOM directly to add an icon and wrapper for styling
    useEffect(() => {
      const wrapSearchInput = () => {
        const itiContainer = getItiElement();
        if (itiContainer) {
          const searchInput = itiContainer?.querySelector(
            '.iti__search-input',
          ) as HTMLInputElement;

          if (searchInput) {
            // Only wrap if not already wrapped
            if (
              !searchInput.parentElement?.classList.contains('search-wrapper')
            ) {
              // Create wrapper div for easier styling
              const wrapper = document.createElement('div');
              wrapper.className = 'search-wrapper';
              searchInput.placeholder = 'Search...';

              // Create search icon element
              const iconSpan = document.createElement('span');
              iconSpan.className = 'search-icon';
              // eslint-disable-next-line react/no-deprecated
              ReactDOM.render(<Search size="xsmall" />, iconSpan);

              // eslint-disable-next-line no-unused-expressions
              searchInput.parentNode?.insertBefore(wrapper, searchInput);
              wrapper.appendChild(iconSpan);
              wrapper.appendChild(searchInput);
            }
          }
        }
      };

      wrapSearchInput();
    }, []);

    // Configure intl-tel-input settings
    const initOptions: TelInputOptions = {
      strictMode: true,
      allowDropdown: !dropdownDisabled,
      initialCountry: initialCountry,
      excludeCountries: excludedCountriesIso,
      onlyCountries: includedCountriesIso,
      countryOrder: prioritizedCountriesIso,
      separateDialCode: separateDialCode || dropdownDisabled,
      countrySearch: listedCountries.length >= 10,
    };

    // Converts error code coming from plugin to readable error messaging
    // TODO: Thoughts?
    const onChangeErrorCodeHandler = (errorCode: number | null) => {
      const errorKey =
        errorCode !== null
          ? Object.keys(validationError).find(
              (key) =>
                validationError[key as keyof typeof validationError] ===
                errorCode,
            ) || null
          : null;
      onChangeErrorCode(errorKey);
    };

    const getCurrentCountry = () => {
      return ref.current?.getInstance()?.getSelectedCountryData();
    };

    return (
      <div
        className={cx(
          stylesheet.root,
          { [stylesheet[inputSize]]: inputSize && inputSize !== 'large' },
          className,
          {
            [stylesheet.error]: !!error,
            [stylesheet.warning]: !!warning,
          },
        )}
      >
        <div className={stylesheet.before}>
          {required && label && (
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            <span className={stylesheet.requiredMarker}>*</span>
          )}
          {label && (
            <label
              className={cx(
                'mcds-label-default',
                hideLabel && 'wink-visually-hidden',
                disabled && 'disabled',
              )}
              id={labelId}
              htmlFor={id}
            >
              {label}
            </label>
          )}
          {miscText &&
            (typeof miscText === 'string' ? (
              <span className={stylesheet.secondary}>{miscText}</span>
            ) : (
              <div className={cx(stylesheet.secondary, stylesheet.nonText)}>
                {miscText}
              </div>
            ))}
        </div>
        <IntlTelInput
          ref={inputPhoneRef}
          initialValue={initialValue}
          disabled={disabled}
          initOptions={initOptions}
          onChangeNumber={onChangeNumber}
          onChangeValidity={onChangeValidity}
          onChangeCountry={() =>
            onChangeCountry(getCurrentCountry() as Country)
          }
          onChangeErrorCode={onChangeErrorCodeHandler}
          usePreciseValidation={false}
          inputProps={{
            ...props,
            className: cx(
              stylesheet.telInput,
              { [stylesheet[inputSize]]: inputSize && inputSize !== 'large' },
              {
                [stylesheet.dropdownDisabled]: dropdownDisabled,
                [stylesheet.disabledInput]: disabled,
                [stylesheet.readOnlyInput]: readOnly,
              },
            ),
            readOnly: readOnly,
            id,
            placeholder,
            'aria-describedby': error || warning || helpText ? helpTextId : '',
            'aria-labelledby': label ? labelId : undefined,
            'aria-invalid': error ? 'true' : 'false',
          }}
        />
        {error ? (
          <div
            id={helpTextId}
            className={cx(stylesheet.after, stylesheet.errorMessage)}
          >
            {typeof error === 'string' ? (
              <IconBadge type="error">{error}</IconBadge>
            ) : (
              error
            )}
          </div>
        ) : warning && typeof warning === 'string' ? (
          <div
            id={helpTextId}
            className={cx(stylesheet.after, stylesheet.warningMessage)}
          >
            <IconBadge type="warning">{warning}</IconBadge>
          </div>
        ) : helpText ? (
          <div
            id={helpTextId}
            className={cx(stylesheet.after, stylesheet.secondary)}
          >
            {helpText}
          </div>
        ) : null}

        {/* Portal for custom arrow */}
        {portalTarget &&
          createPortal(
            <ChevronDown className={stylesheet.customArrow} size="xsmall" />,
            portalTarget,
          )}
      </div>
    );
  },
);

export default InputPhone;
